import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useEquipmentProjects } from "@/hooks/useEquipmentProjects";
import { format } from "date-fns";
import { formatProjectName } from "@/lib/format-project-name";
import {
  splitUpcomingAndPastProjects,
  todayUtcMs,
} from "@/lib/upcoming-projects";
import type { ProjectWithRelations } from "@/hooks/useProjects";

interface EquipmentProjectsListProps {
  equipmentId: string;
}

function ProjectRow({ project }: { project: ProjectWithRelations }) {
  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex items-center justify-between p-3 rounded-lg border text-inherit no-underline hover:bg-muted/50 transition-colors"
      data-testid={`equipment-project-${project.id}`}
    >
      <div>
        <p
          className="font-medium"
          data-testid={`equipment-project-name-${project.id}`}
        >
          {formatProjectName(project.name, project.event_start_date)}
        </p>
        <p className="text-xs text-muted-foreground">
          {project.event_start_date &&
            format(new Date(project.event_start_date), "MMM d, yyyy")}
          {project.event_end_date &&
            ` - ${format(new Date(project.event_end_date), "MMM d, yyyy")}`}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {project.status && (
          <Badge variant="outline" className="capitalize">
            {project.status.replace("_", " ")}
          </Badge>
        )}
        {project.types?.map((type: string) => (
          <Badge key={type} variant="secondary" className="capitalize">
            {type}
          </Badge>
        ))}
      </div>
    </Link>
  );
}

function ProjectSection({
  title,
  projects,
  emptyText,
  testId,
}: {
  title: string;
  projects: ProjectWithRelations[];
  emptyText: string;
  testId: string;
}) {
  return (
    <section className="space-y-2" data-testid={testId}>
      <h3 className="text-sm font-semibold text-muted-foreground">
        {title} ({projects.length})
      </h3>
      {projects.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">{emptyText}</p>
      ) : (
        projects.map((project) => (
          <ProjectRow key={project.id} project={project} />
        ))
      )}
    </section>
  );
}

/** Past projects, collapsed by default to keep the list short. */
function PastProjectsSection({
  projects,
}: {
  projects: ProjectWithRelations[];
}) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section className="space-y-2" data-testid="equipment-projects-past">
        <CollapsibleTrigger
          className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
          data-testid="equipment-projects-past-toggle"
        >
          <ChevronRight
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              open && "rotate-90",
            )}
          />
          Past Projects ({projects.length})
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-2">
          {projects.map((project) => (
            <ProjectRow key={project.id} project={project} />
          ))}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}

export function EquipmentProjectsList({
  equipmentId,
}: EquipmentProjectsListProps) {
  const { data: projects = [], isLoading } = useEquipmentProjects(equipmentId);
  const { upcoming, past } = useMemo(
    () => splitUpcomingAndPastProjects(projects, todayUtcMs()),
    [projects],
  );

  if (isLoading) {
    return (
      <p className="text-muted-foreground text-center py-8">
        Loading projects...
      </p>
    );
  }

  if (projects.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8">
        No projects use this equipment yet.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <ProjectSection
        title="Upcoming Projects"
        projects={upcoming}
        emptyText="No upcoming projects."
        testId="equipment-projects-upcoming"
      />
      {past.length > 0 && <PastProjectsSection projects={past} />}
    </div>
  );
}
